import { equals } from 'ramda';
import type { Path } from 'slate';
import { Editor, Element, Text, Transforms } from 'slate';

import { addSourceToEditOutput } from './addSourceToEditOutput';
import { convertParagraphToDiffs } from './convertParagraphToDiffs';
import { insertSuggestionMarkersFromRangeMarkers } from './insertSuggestionMarkersFromRangeMarkers';
import { debugApply } from '../../../../debug/loggers';
import type { RedlineEdit } from '../../../../redlines/redline-shapes';
import { generateSuggestedEditGroupId } from '../../../helpers';
import type {
  AiChatSuggestionSource,
  AlineText,
  InsertEdit,
  ParagraphElement,
  SuggestionMarkerStartElement,
} from '../../../internal';
import { AlineTransforms, Queries } from '../../../internal';
import type { MakeSuggestionSource, SuggestionSource } from '../../../types';
import { layeredChangesToAlineChanges } from '../../ai-assistant-transforms';
import { RANGE_END, RANGE_START } from '../../layered-diffs/constants';
import { extractRangeMarkers } from '../../layered-diffs/extractRangeMarkers';
import { layerInParagraphChanges } from '../../layered-diffs/layerInParagraphChanges';
import type { DiffSegment, DiffSegmentInsert } from '../../layered-diffs/types';
import { resolveParagraphEntryByLocationAnchor } from '../resolve-location-anchor';

type SlateNoiseDebugFields = Pick<AlineText, 'suggestedEdits' | 'text'> &
  Pick<ParagraphElement, 'children' | 'locationId' | 'type'> &
  Pick<
    SuggestionMarkerStartElement,
    'authorId' | 'markerId' | 'reasoning' | 'suggestionActionType' | 'suggestionId'
  > &
  Pick<InsertEdit, 'source'> &
  Pick<DiffSegmentInsert, 'id' | 'op'> &
  Pick<AiChatSuggestionSource, 'groupId' | 'messageId' | 'threadId'>;

const SLATE_NOISE_DEBUG_KEYS = [
  'text',
  'type',
  'children',
  'suggestedEdits',
  'markerId',
  'suggestionId',
  'suggestionActionType',
  'reasoning',
  'authorId',
  'locationId',
  'op',
  'id',
  'source',
  'groupId',
  'messageId',
  'threadId',
] satisfies Array<keyof SlateNoiseDebugFields>;

const SLATE_NOISE_DEBUG_KEY_SET: ReadonlySet<string> = new Set(SLATE_NOISE_DEBUG_KEYS);

function debugDumpParagraph(label: string, editor: Editor, paragraphPath: number[]): void {
  if (!debugApply.enabled) return;
  /**
   * Re-walk the path so we pick up any mutations applied after the original
   * entry was captured.
   */
  let node: unknown = editor;
  try {
    for (const idx of paragraphPath) {
      node = (node as { children: unknown[] }).children[idx];
    }
  } catch {
    node = '<failed to walk paragraphPath>';
  }
  debugApply(
    '---- applyInParagraphEditOutput paragraph dump: %s ----\n%s',
    label,
    JSON.stringify(stripSlateNoise(node), null, 2)
  );
}

/**
 * Strips formatting/styling properties from a slate tree so the dump focuses
 * on what actually drives suggestion-marker placement. We keep text content,
 * element type, ids, suggestedEdits attribution, and the recursive children
 * structure. Everything else (font, color, format flags, timestamps on
 * suggestedEdits) is removed.
 */
function stripSlateNoise(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stripSlateNoise);
  if (value === null || typeof value !== 'object') return value;
  const obj = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(obj)) {
    if (!SLATE_NOISE_DEBUG_KEY_SET.has(key)) continue;
    out[key] = stripSlateNoise(val);
  }
  return out;
}

export interface ApplyInParagraphEditOutputOptions {
  /**
   * The author ID for the suggested edits (typically the AI or system user)
   */
  authorId: string;
  /**
   * The structured edit output from the LLM (must have category: 'in-paragraph')
   */
  editOutput: RedlineEdit;
  /**
   * Server-generated groupIds for each action, in order. These are always
   * generated on the server and must be passed through to maintain consistent
   * tracking between the document, reactive vars, and the database.
   */
  groupIds: string[];
  /**
   * Builds the source stamp for each suggestion group — the listed groupIds
   * and any synthetic markers minted during layering alike.
   */
  makeSource: MakeSuggestionSource;
  /**
   * The full paragraph text from the LLM with `RANGE_START`/`RANGE_END` pairs
   * around each marked region (one pair per action, in the same order).
   *
   * The layering pipeline (`layerInParagraphChanges`) diffs the paragraph
   * against this text and attributes the changes to each marked region.
   */
  reviewText: string;
}

/**
 * Applies an in-paragraph structured edit output to the editor.
 *
 * This function takes the structured output from an LLM (with category 'in-paragraph')
 * and applies the changes as suggested edits with suggestion markers.
 *
 * The flow:
 * 1. Finds the paragraph by locationId (from editOutput.edit.id)
 * 2. Converts the paragraph content to diffs
 * 3. Adds source metadata to each action
 * 4. Merges and layers the diffs from all actions
 * 5. Extracts range markers for suggestion boundaries
 * 6. Applies the text changes as suggested edits
 * 7. Inserts suggestion markers at the range boundaries
 */
export function applyInParagraphEditOutput(
  editor: Editor,
  options: ApplyInParagraphEditOutputOptions
): void {
  const { authorId, editOutput, groupIds, makeSource, reviewText } = options;

  // Validate that this is an in-paragraph edit
  if (editOutput.edit.category !== 'in-paragraph') {
    throw new Error(
      `applyInParagraphEditOutput only supports 'in-paragraph' edits, got '${editOutput.edit.category}'`
    );
  }

  // Find the paragraph by locationId
  const locationId = editOutput.edit.locationAnchor;
  const [paragraph, paragraphPath] = resolveParagraphEntryByLocationAnchor(editor, locationId);

  // Convert the current paragraph content to diffs
  const documentChanges = convertParagraphToDiffs(paragraph);

  // Add source metadata to each action
  const editOutputWithSource = addSourceToEditOutput(editOutput, { groupIds, makeSource });

  /**
   * Layer the new edits onto the existing paragraph diffs.
   * `layerInParagraphChanges` runs a single global diff across the paragraph
   * and attributes inserts/deletes to markers by adjacency.
   */
  const groupIdsForRanges = editOutputWithSource.edit.actions.map(a => a.groupId);
  /**
   * Use the same nanoid-based generator as the redline agent so synthetic
   * marker ids are indistinguishable from LLM-authored groupIds.
   */
  const layeringOptions = { generateImplicitId: generateSuggestedEditGroupId };
  const layered = layerInParagraphChanges(
    documentChanges,
    reviewText,
    groupIdsForRanges,
    layeringOptions
  );
  const layeredChanges = layered.diffs;
  const syntheticMarkers = layered.syntheticMarkers;

  // Extract range markers and normalize the changes
  const result = extractRangeMarkers(layeredChanges);

  /**
   * Prior-id segments in the layered output represent edits that are ALREADY
   * applied in the live Slate paragraph — text nodes with insertNode or
   * deleteNode marks left there by an earlier redline pass. The post-layering
   * pipeline doesn't need to re-apply them; it just needs `applyAlineChanges`
   * to walk past the text they occupy.
   *
   * - Prior `insert` segments (produced by `reattributePriorInserts` in v2
   *   layering): the corresponding Slate text exists with an `insertNode`
   *   mark. `applyAlineChanges` walks the diff in reverse and treats `insert`
   *   changes as NOT consuming live text, so leaving these as `insert` causes
   *   `state.point` to drift behind by their cumulative length and mis-apply
   *   every preceding new edit.
   *
   * - Prior `delete` segments: the corresponding Slate text exists with a
   *   `deleteNode` mark. Re-applying via `AlineTransforms.deleteContent` is
   *   not just redundant — it's actively destructive. The re-applied delete
   *   is dispatched with `source: undefined` (because the prior groupId
   *   isn't in `sourceByGroupId`), and `suggestDeletion` then walks the
   *   range looking for cancellable inserts. If the range overlaps a
   *   same-author no-source insertNode (e.g., a user manual insertion that
   *   a previous AI redline layered a deleteNode on top of), the no-source
   *   delete and the no-source insertNode register as cancellable per
   *   `getIsCancellableEdit`'s first branch, and `rejectSuggestedInsertion`
   *   removes the user's text outright. This is the user→AI→AI chain bug:
   *   the third redline's prior-id delete sweeps up a user's intermediate
   *   insertion because both sides look "no source" to the cancellability
   *   check.
   *
   * Convert both shapes to `equal` so the walker just advances past the
   * already-applied text. Prior authorship stays on the live Slate nodes;
   * the cancellation detector below reads `layeredChanges` (unmodified), so
   * placeholder-coordinate overlap with prior inserts still works.
   */
  const priorEditIds = new Set(
    documentChanges.filter(seg => seg.op !== 'equal').map(seg => seg.id)
  );
  const segmentsForAlineChanges = result.normalized.map(seg =>
    seg.op !== 'equal' && priorEditIds.has(seg.id)
      ? ({ op: 'equal', text: seg.text } satisfies DiffSegment)
      : seg
  );

  /**
   * Cancellations: when the new edit's delete overlaps a prior `insertNode`
   * AND the two edits' sources are cancellable per `getIsCancellableEdit`
   * (same authorId + compatible source types), the prior insertion is
   * REMOVED from the live tree by `suggestDeletion` →
   * `rejectSuggestedInsertion` rather than getting a stacked `deleteNode`
   * mark layered on top. `extractRangeMarkers` counts every delete's text
   * length in the offset, so each such cancellation pushes downstream
   * markers forward by the cancelled chars and they end up too far down the
   * paragraph — or past it, spilling into sibling paragraphs.
   *
   * We detect cancellations by **placeholder-range overlap** between
   * new-id deletes in the layered diff and the prior inserts in the
   * paragraph, both expressed in placeholder-string coordinates. The
   * placeholder string (built in `layerInParagraphChanges`) represents each
   * prior delete as a single sentinel char and each prior insert / equal as
   * its full text. The layered diff's segments preserve those positions in
   * lockstep with the diff between the placeholder string and `reviewText`.
   *
   * The cancelled prior insert tells us BOTH:
   *   - the chars to subtract from downstream marker offsets (the
   *     `overlap` between the new-id delete and the prior-insert slot — not
   *     the new-id delete's full text length, because the delete may cover
   *     equal text too which stays in the live tree as `deleteNode`-marked
   *     text), and
   *   - the marker (if any) enclosing the prior insert, which must be
   *     force-subsumed by the new marker doing the cancellation. The
   *     ranges collapse to a boundary-touching point in post-cancellation
   *     offset space, so `insertSuggestionMarkersFromRangeMarkers`'s
   *     open-interval position check would otherwise miss the link.
   *
   * Replaces the earlier adjacency-to-prior-delete heuristic, which missed
   * cancellations of insert-only markers (markers wrapping a pure prior
   * insertion with no paired prior delete). Without detection, the offset
   * adjustment didn't fire and the new marker's end overshot — sometimes
   * by enough chars to spill past the paragraph and visually engulf
   * unrelated markers in later paragraphs.
   */
  const newEditSource = editOutputWithSource.edit.actions[0]?.source;

  type PriorInsertSlot = {
    endInPlaceholder: number;
    markerId: number | undefined;
    startInPlaceholder: number;
  };

  /**
   * Cancellable prior inserts in the paragraph, ordered by placeholder
   * position. We walk the paragraph the same way `convertParagraphToDiffs`
   * walks it, tracking placeholder offset (1 char per prior delete, full
   * text per equal/insert).
   */
  const priorInsertSlots: PriorInsertSlot[] = [];
  /**
   * Texts of each prior-id delete text node, in document order. Used to
   * compute the placeholder advance of merged prior-delete segments in the
   * layered diff: `mergeAdjacentSegments` merges adjacent same-id deletes,
   * but each merged paragraph entry represents only 1 placeholder char (not
   * its full restored text length).
   */
  const priorDeleteTextsInOrder: string[] = [];

  let currentEnclosingMarkerId: number | undefined;
  let paragraphPlaceholderOffset = 0;

  const handleTextNode = (textNode: AlineText): void => {
    if (!textNode.suggestedEdits?.length) {
      paragraphPlaceholderOffset += textNode.text.length;
      return;
    }
    /**
     * Classify this text node the same way `convertTextToDiffSegment` does —
     * by the FIRST entry in `suggestedEdits`. A `[deleteNode, ...]` node
     * counts as one prior-id delete (1 placeholder char). An `[insertNode,
     * ...]` node counts as a prior insert (full text in placeholder).
     */
    const firstEdit = textNode.suggestedEdits[0];
    if (firstEdit?.type === 'deleteNode') {
      priorDeleteTextsInOrder.push(textNode.text);
      paragraphPlaceholderOffset += 1;
      return;
    }
    if (firstEdit?.type === 'insertNode') {
      const existingSource = firstEdit.source;
      const cancellable =
        (!existingSource && !newEditSource) ||
        (!!existingSource && !!newEditSource && existingSource.type === newEditSource.type);
      if (cancellable) {
        priorInsertSlots.push({
          endInPlaceholder: paragraphPlaceholderOffset + textNode.text.length,
          markerId: currentEnclosingMarkerId,
          startInPlaceholder: paragraphPlaceholderOffset,
        });
      }
      paragraphPlaceholderOffset += textNode.text.length;
      return;
    }
    // Other edit types (e.g. textFormat) are treated as equal — same as
    // `convertTextToDiffSegment`.
    paragraphPlaceholderOffset += textNode.text.length;
  };

  for (const child of paragraph.children) {
    if (Element.isElement(child)) {
      if (
        child.type === 'suggestionMarkerStart' &&
        'markerId' in child &&
        typeof child.markerId === 'number'
      ) {
        currentEnclosingMarkerId = child.markerId;
        continue;
      }
      if (child.type === 'suggestionMarkerEnd') {
        currentEnclosingMarkerId = undefined;
        continue;
      }
      /**
       * Inline element (textfield, link, etc.) — walk its direct text
       * children one level deep so cancellable inserts inside them are
       * visible. We deliberately do NOT recurse into nested elements.
       */
      if (Array.isArray(child.children)) {
        for (const innerChild of child.children) {
          if (Text.isText(innerChild)) handleTextNode(innerChild);
        }
      }
      continue;
    }
    if (Text.isText(child)) handleTextNode(child);
  }

  /**
   * Placeholder-coordinate range for each segment in the layered diff:
   *   - equal: advances by `text.length`.
   *   - prior-id delete: advances by the number of paragraph prior-delete
   *     entries it consumes (each is 1 placeholder char). We match by
   *     consuming text lengths from `priorDeleteTextsInOrder` until the
   *     segment's restored text is exhausted.
   *   - new-id delete: advances by `text.length` (the chars come from the
   *     placeholder string at that position).
   *   - prior-id insert: advances by `text.length`. These segments are
   *     reattributed equals (`reattributePriorInserts`) covering text that
   *     was in the placeholder string at this position.
   *   - new-id insert (including range markers): advances by 0. New text
   *     comes from `reviewText`, not the placeholder.
   */
  const layeredPlaceholderRanges: Array<{ end: number; start: number }> = [];
  {
    let layeredPlaceholderOffset = 0;
    let priorDeleteIdx = 0;
    for (let i = 0; i < layeredChanges.length; i += 1) {
      const seg = layeredChanges[i];
      const start = layeredPlaceholderOffset;
      if (seg.op === 'equal') {
        layeredPlaceholderOffset += seg.text.length;
      } else if (seg.op === 'delete') {
        if (priorEditIds.has(seg.id)) {
          let consumed = 0;
          while (priorDeleteIdx < priorDeleteTextsInOrder.length && consumed < seg.text.length) {
            const t = priorDeleteTextsInOrder[priorDeleteIdx];
            if (t === undefined) break;
            consumed += t.length;
            priorDeleteIdx += 1;
            layeredPlaceholderOffset += 1;
          }
        } else {
          layeredPlaceholderOffset += seg.text.length;
        }
      } else if (seg.op === 'insert' && priorEditIds.has(seg.id)) {
        layeredPlaceholderOffset += seg.text.length;
      }
      // New-id inserts (incl. RANGE_START / RANGE_END) leave the offset alone.
      layeredPlaceholderRanges.push({ end: layeredPlaceholderOffset, start });
    }
  }

  /**
   * For each new-id delete, find every prior-insert slot it overlaps. Each
   * overlapping slot contributes:
   *   - its overlap length to `cancelledChars` (chars removed from the live
   *     tree by `applyAlineChanges`, hence to subtract from downstream
   *     marker offsets);
   *   - its `markerId` to the must-subsume set keyed by the new groupId
   *     (so the cancelling new marker pulls the enclosing prior marker in,
   *     even though their positional ranges collapse to boundary-touching
   *     after cancellation).
   */
  const cancellationDeleteIndices = new Set<number>();
  const cancelledCharsByCancellationIdx = new Map<number, number>();
  const mustSubsumeMarkerIdsByNewGroupId = new Map<string, Set<number>>();
  for (let i = 0; i < layeredChanges.length; i += 1) {
    const seg = layeredChanges[i];
    if (seg.op !== 'delete' || priorEditIds.has(seg.id)) continue;
    const range = layeredPlaceholderRanges[i];
    if (!range) continue;

    let cancelledChars = 0;
    const markerIdsToSubsume: number[] = [];
    for (const slot of priorInsertSlots) {
      const overlapStart = Math.max(range.start, slot.startInPlaceholder);
      const overlapEnd = Math.min(range.end, slot.endInPlaceholder);
      if (overlapEnd <= overlapStart) continue;
      cancelledChars += overlapEnd - overlapStart;
      if (slot.markerId !== undefined) markerIdsToSubsume.push(slot.markerId);
    }
    if (cancelledChars === 0) continue;

    cancellationDeleteIndices.add(i);
    cancelledCharsByCancellationIdx.set(i, cancelledChars);

    if (markerIdsToSubsume.length > 0) {
      const newGroupId = seg.id;
      let set = mustSubsumeMarkerIdsByNewGroupId.get(newGroupId);
      if (!set) {
        set = new Set<number>();
        mustSubsumeMarkerIdsByNewGroupId.set(newGroupId, set);
      }
      for (const markerId of markerIdsToSubsume) set.add(markerId);
    }
  }

  if (cancellationDeleteIndices.size > 0) {
    let cumulativeOffsetInLayered = 0;
    let cumulativeCancelled = 0;
    const offsetAdjustmentByLayeredOffset = new Map<number, number>();
    for (let i = 0; i < layeredChanges.length; i += 1) {
      const seg = layeredChanges[i];
      const isMarker = seg.op === 'insert' && (seg.text === RANGE_START || seg.text === RANGE_END);
      if (isMarker) {
        offsetAdjustmentByLayeredOffset.set(cumulativeOffsetInLayered, cumulativeCancelled);
        continue;
      }
      if (cancellationDeleteIndices.has(i)) {
        cumulativeCancelled += cancelledCharsByCancellationIdx.get(i) ?? 0;
      }
      cumulativeOffsetInLayered += seg.text.length;
    }
    result.rangeMarkers = result.rangeMarkers.map(marker => ({
      ...marker,
      offset: marker.offset - (offsetAdjustmentByLayeredOffset.get(marker.offset) ?? 0),
    }));
  }

  // Convert to aline changes and batch by newline
  const alineChanges = layeredChangesToAlineChanges(segmentsForAlineChanges);
  const batchedChanges = Queries.batchChangesByNewLine(alineChanges);

  /**
   * Build the source map for suggested edits. Synthetic markers (Phase 2) get
   * sources from the same factory as the listed actions so downstream code
   * treats them like LLM-authored markers. Sourceless groups (a
   * user-attributed materialization) add no entry: `applyAlineChanges` then
   * stamps their edits with no source, the plain user-edit shape.
   */
  const sourceByGroupId = new Map<string, SuggestionSource>();
  for (const action of editOutputWithSource.edit.actions) {
    if (action.source) sourceByGroupId.set(action.groupId, action.source);
  }
  for (const synth of syntheticMarkers) {
    const synthSource = makeSource(synth.id);
    if (synthSource) sourceByGroupId.set(synth.id, synthSource);
  }

  debugDumpParagraph('BEFORE applyAlineChanges', editor, paragraphPath);

  /**
   * Apply the changes as suggested edits.
   * `normalizePoints: true` is critical for layered diffs: when state.point
   * lands at offset 0 of a leaf (a boundary equivalent to the end of the
   * previous leaf), it gets normalized to the end of the previous leaf so
   * subsequent deletion ranges don't extend into adjacent text nodes. Without
   * it, suggestDeletion's splitTextAtEdges sees an end-point at offset 0 of
   * the next leaf, treats it as not-at-end, and emits a spurious split_node.
   */
  AlineTransforms.applyAlineChanges(editor, {
    at: Editor.range(editor, paragraphPath),
    authorId,
    changes: batchedChanges,
    normalizePoints: true,
    sourceByGroupId,
  });

  debugDumpParagraph('AFTER applyAlineChanges (before marker insertion)', editor, paragraphPath);

  /**
   * Remove zero-width text leaves left behind by `applyAlineChanges`. The
   * outer caller wraps this whole flow in `withoutNormalizing`, so slate's
   * built-in empty-text cleanup never runs between phases. If we leave them
   * in, marker insertion will emit `split_node` ops adjacent to these leaves
   * and slate-yjs can't reconcile the resulting paths against its merged
   * delta representation, throwing "yTarget spans multiple nodes" during
   * flush.
   */
  removeEmptyTextLeavesInParagraph(editor, paragraphPath);

  /**
   * Build the action metadata map for suggestion markers, including synthetic
   * ones so their (generic) reasoning is shown in the UI alongside the real
   * LLM-authored reasonings. Each entry carries the source so the marker
   * insertion's subsumption-homogenization step can rewrite AI-source edits
   * inside a newly subsumed zone to match the new marker's source.
   */
  const actionMetadataByGroupId = new Map(
    editOutputWithSource.edit.actions.map(action => [
      action.groupId,
      { authorId, reasoning: action.reasoning, source: action.source },
    ])
  );
  for (const synth of syntheticMarkers) {
    actionMetadataByGroupId.set(synth.id, {
      authorId,
      reasoning: synth.reasoning,
      source: makeSource(synth.id),
    });
  }

  /**
   * Insert suggestion markers at the range boundaries. The mustSubsume map
   * covers the cancellation-chain case where the new redline's delete
   * cancelled an `insertNode` that lived inside an existing marker pair —
   * see the comment above where the map is built.
   */
  insertSuggestionMarkersFromRangeMarkers(editor, {
    actionMetadataByGroupId,
    mustSubsumeMarkerIdsByNewGroupId,
    paragraphPath,
    rangeMarkers: result.rangeMarkers,
  });

  /**
   * Marker insertion's own `split_node` / `insert_text` ops can leave
   * additional orphan empty leaves behind (for instance, when the new
   * marker's range subsumes a prior marker that lived adjacent to an
   * inserted leaf). Run the cleanup a second time so the final tree is
   * free of redundant zero-width siblings.
   */
  removeEmptyTextLeavesInParagraph(editor, paragraphPath);

  debugDumpParagraph(
    'AFTER insertSuggestionMarkersFromRangeMarkers (FINAL)',
    editor,
    paragraphPath
  );
}

/**
 * Walks a paragraph's direct children and removes zero-width text leaves
 * that get left behind by `applyAlineChanges` when splitting/inserting
 * around prior pending edits. Slate's built-in paragraph normalization
 * would normally clean them up, but the caller wraps the entire redline
 * flow in `withoutNormalizing`, so we have to do it explicitly here. We
 * only remove a leaf if at least one direct sibling is a non-empty text
 * node, mirroring the existing rule in `paragraph-normalizations.ts`.
 *
 * Two shapes are removed:
 *   1. Empty leaf with no `suggestedEdits`. Pure noise.
 *   2. Empty leaf whose `suggestedEdits` are a subset of an adjacent
 *      non-empty text sibling's. This is the "orphan from split_node at
 *      offset 0" pattern: `AlineTransforms.insertText` and marker
 *      insertion can emit `insert_node` followed by `split_node` at
 *      position 0, which leaves an empty half carrying the same
 *      `insertNode` (or layered `deleteNode + insertNode`) mark as the
 *      newly-inserted text next to it. The mark on the empty leaf is
 *      redundant — every entry is already represented on the non-empty
 *      sibling — and the leaf serves no purpose in the rendered tree.
 *
 * Subset (not strict equality) lets us also remove orphans whose marks
 * are a proper subset of the neighbor's — produced when marker insertion
 * splits a leaf that carries both a prior layered `deleteNode` and a
 * `insertNode`, leaving an empty half with just the `deleteNode`.
 */
function removeEmptyTextLeavesInParagraph(editor: Editor, paragraphPath: Path): void {
  /**
   * Iterate to a fixed point. A single pass can leave behind orphans that
   * only become removable after an earlier orphan is removed (two adjacent
   * empty leaves: each shows the other as its non-text-or-empty neighbor,
   * so neither sees a qualifying non-empty text sibling). Removing the
   * first then re-walking exposes the second's real neighbor.
   */
  while (collectAndRemoveOnePass(editor, paragraphPath)) {
    // keep going
  }
}

function collectAndRemoveOnePass(editor: Editor, paragraphPath: Path): boolean {
  const [paragraph] = Editor.node(editor, paragraphPath);
  if (!Element.isElement(paragraph)) return false;

  const indicesToRemove: number[] = [];
  const children = paragraph.children;

  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (!Text.isText(child)) continue;
    if (child.text !== '') continue;

    const prev = i > 0 ? children[i - 1] : undefined;
    const next = i < children.length - 1 ? children[i + 1] : undefined;
    const prevIsNonEmptyText = !!prev && Text.isText(prev) && prev.text.length > 0;
    const nextIsNonEmptyText = !!next && Text.isText(next) && next.text.length > 0;
    if (!prevIsNonEmptyText && !nextIsNonEmptyText) continue;

    const childEdits = child.suggestedEdits ?? [];
    if (childEdits.length === 0) {
      indicesToRemove.push(i);
      continue;
    }

    /**
     * An empty leaf's edits are redundant whenever a non-empty neighbor
     * already carries all of them — equal sets and proper subsets both
     * qualify. The strict-equal-only check used to miss orphans produced
     * by `insertText`'s `insert_node` + `split_node` sequence, where the
     * orphan ends up holding just the deleteNode while the neighbor
     * carries `[deleteNode, insertNode]` (the prior user insertion).
     */
    const prevSubsumes =
      prevIsNonEmptyText &&
      Text.isText(prev) &&
      childEdits.every(e => (prev.suggestedEdits ?? []).some(pe => equals(e, pe)));
    const nextSubsumes =
      nextIsNonEmptyText &&
      Text.isText(next) &&
      childEdits.every(e => (next.suggestedEdits ?? []).some(ne => equals(e, ne)));
    if (prevSubsumes || nextSubsumes) {
      indicesToRemove.push(i);
    }
  }

  if (indicesToRemove.length === 0) return false;

  for (let k = indicesToRemove.length - 1; k >= 0; k--) {
    Transforms.removeNodes(editor, {
      at: [...paragraphPath, indicesToRemove[k]],
      voids: true,
    });
  }
  return true;
}
